import {
  type CollisionDetection,
  closestCenter,
  type Modifier,
  pointerWithin,
} from "@dnd-kit/core";

/**
 * Custom collision detection algorithm that ensures proper drop behavior
 * when dragging beyond table bounds.
 */
export const constrainedClosestCenter: CollisionDetection = (args) => {
  const { active, droppableContainers, pointerCoordinates } = args;

  // Scope to the active SortableContext (pinned vs unpinned)
  const activeContainerId =
    active?.data?.current?.sortable?.containerId ?? null;
  const scopedContainers = activeContainerId
    ? droppableContainers.filter(
        (c) => c.data.current?.sortable?.containerId === activeContainerId,
      )
    : droppableContainers;

  // Resolve table bounds once
  const tableContainer =
    document.querySelector("[data-table-container]") ||
    document.querySelector(".table-fixed") ||
    document.querySelector("table");
  const tableRect = (
    tableContainer as HTMLElement | null
  )?.getBoundingClientRect();

  // Helper: pick extreme container (leftmost/rightmost) within scope
  function pickExtreme(dir: "left" | "right") {
    const valid = scopedContainers.filter((c) => c.rect.current);
    if (valid.length === 0) return null;
    return valid.reduce((acc, c) => {
      const accEdge =
        dir === "left" ? acc.rect.current!.left : acc.rect.current!.right;
      const cEdge =
        dir === "left" ? c.rect.current!.left : c.rect.current!.right;
      return dir === "left"
        ? cEdge < accEdge
          ? c
          : acc
        : cEdge > accEdge
          ? c
          : acc;
    });
  }

  // Prefer pointer-based collisions to avoid bias from wide drag rects
  if (pointerCoordinates) {
    if (tableRect) {
      if (pointerCoordinates.x < tableRect.left) {
        const leftmost = pickExtreme("left");
        return leftmost
          ? [
              {
                id: leftmost.id,
                data: { droppableContainer: leftmost, value: 0 },
              },
            ]
          : [];
      }
      if (pointerCoordinates.x > tableRect.right) {
        const rightmost = pickExtreme("right");
        return rightmost
          ? [
              {
                id: rightmost.id,
                data: { droppableContainer: rightmost, value: 0 },
              },
            ]
          : [];
      }
    }

    // 1) Pointer hit testing within scoped list
    const pointerHits = pointerWithin({
      ...args,
      droppableContainers: scopedContainers,
    });
    if (pointerHits.length > 0) return pointerHits;

    // 2) Fallback: closest center using a 1x1 virtual rect at pointer
    const virtualRect = {
      left: pointerCoordinates.x,
      right: pointerCoordinates.x,
      top: pointerCoordinates.y,
      bottom: pointerCoordinates.y,
      width: 0,
      height: 0,
      x: pointerCoordinates.x,
      y: pointerCoordinates.y,
    } as const;
    return closestCenter({
      ...args,
      droppableContainers: scopedContainers,
      collisionRect: virtualRect,
    });
  }

  // Final fallback
  return closestCenter({ ...args, droppableContainers: scopedContainers });
};

/**
 * DnD Kit modifier to constrain dragging to table bounds
 */
export const restrictToTableBounds: Modifier = ({
  transform,
  draggingNodeRect,
  containerNodeRect,
}) => {
  if (!draggingNodeRect || !containerNodeRect) {
    return transform;
  }

  // Find the table container
  const tableContainer =
    document.querySelector("[data-table-container]") ||
    document.querySelector(".table-fixed") ||
    document.querySelector("table");

  if (!tableContainer) {
    return transform;
  }

  // Get the table's actual dimensions, not just the visible viewport portion
  const tableRect = tableContainer.getBoundingClientRect();
  const tableScrollContainer =
    tableContainer.closest('[style*="overflow"]') ||
    tableContainer.parentElement;

  if (!tableScrollContainer) {
    return transform;
  }

  const scrollContainerRect = tableScrollContainer.getBoundingClientRect();

  // Calculate the left boundary (don't go left of the scroll container)
  const leftBound = scrollContainerRect.left - draggingNodeRect.left;

  // Calculate the right boundary (allow dragging to the full table width, not just visible viewport)
  // Use scrollWidth to get the full width including off-screen content
  const tableElement = tableContainer as HTMLElement;
  const tableFullWidth = tableElement.scrollWidth || tableElement.offsetWidth;
  const tableLeft = tableRect.left;
  const fullTableRight = tableLeft + tableFullWidth;
  const rightBound = fullTableRight - draggingNodeRect.right;

  return {
    ...transform,
    x: Math.max(leftBound, Math.min(rightBound, transform.x)),
  };
};
